import { ChangeDetectorRef, Component, Input } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Game, GameDataSource, GameQuery, GameService, GameTicketQuery, Ticket, TICKET_TRANSFER_STATUS, TicketFlag, TicketQuery, TicketService, toNumber } from '@fgb/core';
import { BehaviorSubject, map, Observable, switchMap } from 'rxjs';
import { filterBy, FilterFn } from '@fgb/portal-component-library/src/lib/shared/utilities';
import { SortOptions } from '@fgb/portal-component-library/src/lib/directives';
import { DateTime } from 'luxon';

@Component({
  selector: 'fgbcl-ticket-selection-list',
  templateUrl: './ticket-selection-list.component.html',
  styleUrl: './ticket-selection-list.component.scss'
})
export class TicketSelectionListComponent {
  gameNumber: number;
  ticketList$: Observable<Ticket[]>;
  showTicketColumn1: boolean = false;
  showTicketColumn2: boolean = false;
  minDateString: string = '0001-01-01T00:00:00';
  ticketStatus = TICKET_TRANSFER_STATUS;
  gameDetails$: Observable<any>;
  selectedTickets$: Observable<Ticket[]>;
  anySelected$: Observable<boolean>;
  transferWindowExist$: Observable<boolean>;
  isWindowOpen$: Observable<boolean>;
  @Input() activeFilter: FilterFn[] = [
    filterBy({ Flag: TicketFlag.ACTIVE })
  ];
  @Input() transferredFilter: FilterFn[] = [
    (game: any) => TicketFlag.TRANSFERRED === this.checkFlag(game.Flag, TicketFlag.TRANSFERRED)
  ];
  @Input() sortOptions: SortOptions<any>;
  activeTickets: Ticket[] = [];
  showSelectAll: boolean = false;
  showTransferTitle: boolean = false;
  showActiveTitle: boolean = false;
  refreshSubject: BehaviorSubject<void> = new BehaviorSubject<void>(undefined);
  
  constructor(
    private route: ActivatedRoute,
    private ticketQuery: TicketQuery,
    private gameQuery: GameQuery,
    private gameService: GameService,
    private gameTicketQuery: GameTicketQuery,
    private ticketService: TicketService,
    private _cd: ChangeDetectorRef
  ) {}

  ngOnInit() {
    this.gameNumber = toNumber(this.route.snapshot.paramMap.get('gameNumber'));

    this.ticketList$ = this.ticketQuery.selectTicketsForGame(this.gameNumber);
    this.gameDetails$ = this.gameTicketQuery.selectGameDetailsByGameNumber(this.gameNumber);
    const ticket0$ = this.refresh$.pipe(switchMap(() => this.ticketQuery.selectDefaultTicket(this.gameNumber))) ;
    this.transferWindowExist$ = ticket0$.pipe(map((ticket) => this.transferWindowExists(ticket?.StartTime, ticket?.EndTime)));
    this.isWindowOpen$ = ticket0$.pipe(map((ticket) => this.isWindowOpen(ticket?.StartTime, ticket?.EndTime)));
    this.fetchGamesAndSetActiveGame();
  }

  /**
   * Fetch tickets and refresh the view.
   */
  refresh(): void{
    this.ticketService.fetchTickets();
    this.refreshSubject.next();
  }

  /**
   * Will fetch data if not cached but will also
   * set a game as active.
   */
  fetchGamesAndSetActiveGame() {
    let activeGame = this.gameQuery.getActive() as Game;
    if (!activeGame) {
      this.gameService.fetchGamesAndSetActiveGame(this.gameNumber, GameDataSource.ExternalRef);
    }
  }

  private transferWindowExists(startDate: Date | string | undefined, endDate: Date | string | undefined) {
    const minDateString = DateTime.fromISO(this.minDateString, { zone: 'utc' });
    
    return !(
      DateTime.fromISO(startDate as string, { zone: 'utc' }).toISODate() === minDateString.toISODate()
      || DateTime.fromISO(endDate as string, { zone: 'utc' }).toISODate() === minDateString.toISODate()
    );
  }

  private isWindowOpen(startDate: string | Date | undefined, endDate: string | Date | undefined) {
    return DateTime.fromISO(startDate as string, { zone: 'utc' }) < DateTime.utc() && DateTime.fromISO(endDate as string, { zone: 'utc' }) > DateTime.utc();
  }

  private checkFlag(status: number, flag: number) {
    return status & flag;
  }

  onActiveDataSourceChange(data: Ticket[]) {
    this.activeTickets = data;
    this.showSelectAll = data?.length > 1;
    this.showActiveTitle = data?.length > 0;
    this._cd.detectChanges();
  }

  onTransferredDataChange(data: Ticket[]) {
    this.showTransferTitle = data?.length > 0;
    this._cd.detectChanges();
  }

  get anyUnselected() {
    return this.activeTickets.some((t) => !t.selected && t.Flag === TicketFlag.ACTIVE);
  }

  get refresh$() {
    return this.refreshSubject.asObservable();
  }

  selectAll() {
    this.ticketService.toggleSelectAllTickets(this.gameNumber, this.anyUnselected);
  }
}
